import { useContext } from 'react';

import {
    type QueryClient,
    type QueryFunction,
    type QueryKey,
    type UseQueryOptions,
    keepPreviousData,
    useQueryClient,
} from '@tanstack/react-query';

import { invalidateWorkspaces } from './cache.ts';
import { WorkspaceIdContext } from './client.ts';
import type { Home, PagedRecapListEntry, PagedSourceListEntry, Recap, Source, Start } from './generated/schemas.ts';
import { POLL_INTERVAL_MS, pollWhileRunning } from './polling.ts';

// What the generated query hooks (orval.config.ts) do beyond the contract, by operation: their key in the cache, the
// options the endpoint needs, and the polling of what's processing or syncing.

const ATTENTION_INTERVAL_MS = 60_000;

// The first email can take a while to be forwarded, so it's asked for less often than what's then being read.
const FORWARD_POLL_INTERVAL_MS = 5000;

const startPollInterval = (start: Start | null | undefined) => {
    if (!start) return false;
    if (start.step === 'forward') return FORWARD_POLL_INTERVAL_MS;
    const isWaiting =
        (start.step === 'clients' && !start.suggestions?.status) ||
        start.suggestions?.status === 'processing' ||
        (start.suggestions?.processing_count ?? 0) > 0 ||
        start.drafts.some((draft) => draft.extraction_status === 'processing');
    return isWaiting ? POLL_INTERVAL_MS : false;
};

const QUERY_OPTIONS: Record<string, Partial<UseQueryOptions>> = {
    // Paged lists keep the page shown while the next one loads, and are fetched again while an item on the page is
    // processing.
    listRecaps: {
        placeholderData: keepPreviousData,
        refetchInterval: (query) =>
            (query.state.data as PagedRecapListEntry | undefined)?.items.some(
                (item) => item.extraction_status === 'processing'
            )
                ? POLL_INTERVAL_MS
                : false,
    },
    getRecap: {
        refetchInterval: (query) =>
            (query.state.data as Recap | undefined)?.extraction_status === 'processing' ? POLL_INTERVAL_MS : false,
    },
    listSources: {
        placeholderData: keepPreviousData,
        refetchInterval: (query) =>
            (query.state.data as PagedSourceListEntry | undefined)?.items.some(
                (item) => item.extraction_status === 'processing'
            )
                ? POLL_INTERVAL_MS
                : false,
    },
    // Written by everything else, so it's fetched again whenever it's opened.
    listActivities: { placeholderData: keepPreviousData, staleTime: 0 },
    listEmployeeRecapActivities: { placeholderData: keepPreviousData, staleTime: 0 },
    // Gathered from every recap, which other pages change, so it's fetched again whenever it's opened.
    getHome: {
        staleTime: 0,
        // While its start waits for emails, for them to be read and sorted, or for its drafts (plan task 124); like
        // all polling, not while the tab is hidden.
        refetchInterval: (query) => startPollInterval((query.state.data as Home | undefined)?.start),
    },
    // Its recaps' dots change with what members do, so it's fetched again whenever it's opened.
    getEmployeeHome: { staleTime: 0 },
    listEmployeeRecaps: { placeholderData: keepPreviousData },
    // What the rail counts changes with what others and the pipeline do, so it's asked again every minute and when
    // the window gets focus (plan task 61); every change made here refetches it too (mutations.ts).
    getAttention: { staleTime: 0, refetchInterval: ATTENTION_INTERVAL_MS, refetchOnWindowFocus: true },
    getEmployeeAttention: { staleTime: 0, refetchInterval: ATTENTION_INTERVAL_MS, refetchOnWindowFocus: true },
    getSource: {
        refetchInterval: (query) =>
            (query.state.data as Source | undefined)?.extraction_status === 'processing' ? POLL_INTERVAL_MS : false,
    },
    // What's a source already changes with every pick and sync, so it's fetched again whenever it's opened; a new
    // search keeps the list shown until it answers (plan task 100).
    listPickItems: { placeholderData: keepPreviousData, staleTime: 0 },
};

type SyncEnded = (queryClient: QueryClient, workspaceId: string | null) => void;

// Lists whose items sync in the background (`is_syncing`): polled while one does, and what a sync brings is fetched
// again when it ends.
const SYNCING_LISTS: Record<string, SyncEnded> = {
    // What a feed's sync fetched shows up as processing sources, whose own polling takes over from there; a feed of
    // customers brings companies, which the sync may have linked to clients (plan/DECISIONS.md D176).
    listConnections: (queryClient, workspaceId) => {
        void queryClient.invalidateQueries({ queryKey: ['workspaces', workspaceId, 'recaps'] });
        void queryClient.invalidateQueries({ queryKey: ['workspaces', workspaceId, 'sources'] });
        void queryClient.invalidateQueries({ queryKey: ['workspaces', workspaceId, 'external-companies'] });
        void queryClient.invalidateQueries({ queryKey: ['workspaces', workspaceId, 'clients'] });
        invalidateWorkspaces(queryClient);
    },
    // A client's employee's own feeds only send (plan task 35), so nothing else changes when they're done.
    listEmployeeConnections: () => {},
};

// A workspace's query without a path id reads the collection (a list, its stats …) and sits below 'list', so a change
// refreshes every page and figure of it without refetching the items it just wrote (plan/docs/structure.md "Generated
// API schemas"). The generated keys start with 'workspace' (orval.config.ts drops the id), the cache keeps them per
// workspace.
const toCacheKey = (queryKey: QueryKey, args: object, workspaceId: string | null): QueryKey => {
    const [scope, resource, ...rest] = queryKey;
    if (scope !== 'workspace') return queryKey;
    const isCollection = Object.keys(args).every((name) => name === 'params');
    return ['workspaces', workspaceId, resource, ...(isCollection ? ['list'] : []), ...rest];
};

// orval calls this with the query's options, the operation's arguments (the path ids by name, `params` for the query
// string) and the operation.
export const useQueryBehavior = <Options extends { queryKey: QueryKey; queryFn?: unknown }>(
    options: Options,
    args: object,
    operation: { url: string; operationId: string; operationName: string }
): Options => {
    const workspaceId = useContext(WorkspaceIdContext);
    const queryClient = useQueryClient();
    const queryKey = toCacheKey(options.queryKey, args, workspaceId);
    const onSyncEnded = SYNCING_LISTS[operation.operationName];

    return {
        ...QUERY_OPTIONS[operation.operationName],
        ...options,
        queryKey,
        ...(onSyncEnded &&
            typeof options.queryFn === 'function' &&
            pollWhileRunning(queryClient, {
                queryKey,
                queryFn: options.queryFn as QueryFunction<{ id: string; is_syncing: boolean }[]>,
                isRunning: (item) => item.is_syncing,
                onFinished: () => onSyncEnded(queryClient, workspaceId),
            })),
    };
};
